import { cleanPhone } from '@/core/format';
import {
    BonusAccountSortField,
    type AccrueBonusPointsRequest,
    type GetBonusAccountsRequest,
    type SpendBonusPointsRequest,
} from '@/core/generated/api';
import { asNonEmptyFilterString, getDataTableFilterValue } from '@/shared/lib/dataTableFilters';
import type { BonusOperationFormValues } from './model/schema';
import type { BonusAccountListParams } from './model/types';

const SORT_FIELDS: Readonly<Record<string, BonusAccountSortField>> = {
    bonusAccountNumber: BonusAccountSortField.BonusAccountNumber,
    state: BonusAccountSortField.State,
    activeBalance: BonusAccountSortField.ActiveBalance,
};

/** Проверяет положительное количество бонусных баллов */
const requirePositiveAmount = (value: number | null): number => {
    if (value === null || value <= 0) {
        throw new Error('Количество бонусных баллов должно быть больше 0');
    }
    return value;
};

/** Подготавливает общие данные операции с бонусами */
const bonusOperationBaseRequest = (
    values: BonusOperationFormValues,
    clientId: string,
    operationId: string,
) => ({
    clientId,
    operationId,
    amount: requirePositiveAmount(values.amount),
    description: values.description.trim() || null,
});

/** Преобразует параметры таблицы в данные для получения бонусных счетов */
export const bonusAccountTableFiltersToListRequest = (
    {
        filters,
        take,
        skip,
        sortField,
        sortOrder,
    }: BonusAccountListParams
): GetBonusAccountsRequest => {
    const phone = asNonEmptyFilterString(getDataTableFilterValue(filters, 'client.phone'));

    return {
        takeCount: take && take > 0
            ? take
            : 10,
        skipCount: skip && skip > 0
            ? skip
            : 0,
        bonusAccountNumberSearch: asNonEmptyFilterString(
            getDataTableFilterValue(filters, 'bonusAccountNumber'),
        ),
        clientPhoneSearch: phone
            ? cleanPhone(phone)
            : undefined,
        sortBy: sortField
            ? SORT_FIELDS[sortField]
            : BonusAccountSortField.BonusAccountNumber,
        ascending: sortOrder !== -1,
    };
};

/** Подготавливает данные для начисления бонусов */
export const bonusOperationFormValuesToAccrueRequest = (
    values: BonusOperationFormValues,
    clientId: string,
    operationId: string,
): AccrueBonusPointsRequest => bonusOperationBaseRequest(values, clientId, operationId);

/** Подготавливает данные для списания бонусов */
export const bonusOperationFormValuesToSpendRequest = (
    values: BonusOperationFormValues,
    clientId: string,
    operationId: string,
): SpendBonusPointsRequest => bonusOperationBaseRequest(values, clientId, operationId);
